import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";
import { slides } from "../data/content";
import gsap from "gsap";

const Carousel = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [entered, setEntered] = useState(false);

  const charsRef = useRef([]);
  const descRef = useRef(null);
  const controlsRef = useRef(null);
  const line1Ref = useRef(null);
  const entranceDone = useRef(false);
  const isAnimating = useRef(false);
  const activeIndexRef = useRef(0);

  activeIndexRef.current = activeIndex;
  const current = slides[activeIndex];

  // === ENTRANCE ===
  useEffect(() => {
    const tl = gsap.timeline({
      delay: 1.2,
      onComplete: () => {
        entranceDone.current = true;
        setEntered(true);
      },
    });

    tl.fromTo(
      line1Ref.current,
      { yPercent: 100, opacity: 0 },
      { yPercent: 0, opacity: 1, duration: 0.9, ease: "power4.out" }
    );

    tl.fromTo(
      charsRef.current.filter(Boolean),
      { yPercent: 130, opacity: 0 },
      {
        yPercent: 0,
        opacity: 1,
        duration: 0.6,
        stagger: 0.04,
        ease: "power4.out",
      },
      "-=0.5"
    );

    tl.fromTo(
      descRef.current,
      { y: 20, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.7, ease: "power3.out" },
      "-=0.2"
    );

    tl.fromTo(
      controlsRef.current,
      { opacity: 0 },
      { opacity: 1, duration: 0.6 },
      "-=0.3"
    );

    return () => tl.kill();
  }, []);

  // === CYCLING ANIMATION ===
  useLayoutEffect(() => {
    if (!entranceDone.current) return;
    const validChars = charsRef.current.filter(Boolean);
    if (!validChars.length) return;

    gsap.fromTo(
      validChars,
      { yPercent: 130, opacity: 0 },
      {
        yPercent: 0,
        opacity: 1,
        duration: 0.55,
        stagger: 0.035,
        ease: "power4.out",
        onComplete: () => {
          isAnimating.current = false;
        },
      }
    );

    gsap.fromTo(
      descRef.current,
      { y: 12, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.5, ease: "power3.out", delay: 0.15 }
    );
  }, [activeIndex]);

  // === NAVIGATE WITH EXIT ===
  const animateToSlide = useCallback((newIndex) => {
    if (isAnimating.current) return;
    isAnimating.current = true;

    const validChars = charsRef.current.filter(Boolean);

    if (validChars.length && entranceDone.current) {
      gsap.to(validChars, {
        yPercent: -120,
        opacity: 0,
        duration: 0.3,
        stagger: 0.02,
        ease: "power3.in",
        onComplete: () => setActiveIndex(newIndex),
      });
      gsap.to(descRef.current, {
        y: -8,
        opacity: 0,
        duration: 0.25,
        ease: "power2.in",
      });
    } else {
      setActiveIndex(newIndex);
    }
  }, []);

  const next = () => {
    const i = (activeIndexRef.current + 1) % slides.length;
    animateToSlide(i);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 10000);
  };

  const prev = () => {
    const i =
      (activeIndexRef.current - 1 + slides.length) % slides.length;
    animateToSlide(i);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 10000);
  };

  useEffect(() => {
    if (isPaused || !entered) return;
    const interval = setInterval(() => {
      if (!isAnimating.current) {
        const i = (activeIndexRef.current + 1) % slides.length;
        animateToSlide(i);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [isPaused, entered, animateToSlide]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  charsRef.current = [];

  return (
    <div
      className="flex flex-col items-center text-center w-full max-w-4xl mx-auto select-none"
      role="region"
      aria-roledescription="carousel"
      aria-label="Key qualities carousel"
    >
      {/* Prefix line */}
      <div className="overflow-hidden mb-2 md:mb-3">
        <p
          ref={line1Ref}
          className="font-sans text-sm md:text-base font-medium tracking-[0.3em] uppercase text-brand-muted/50 opacity-0 select-none"
        >
          We make it
        </p>
      </div>

      {/* Cycling word */}
      <div className="overflow-hidden leading-[0.9] py-1 md:py-2 select-none">
        <h2 className="flex justify-center">
          <span className="sr-only">{current.word}</span>
          {current.word.split("").map((char, i) => (
            <span
              key={`${activeIndex}-${i}`}
              ref={(el) => {
                if (el) charsRef.current.push(el);
              }}
              className="inline-block font-orbit font-black text-brand-cream uppercase opacity-0 select-none"
              style={{
                fontSize: "clamp(3.5rem, 14vw, 12rem)",
                letterSpacing: "0.04em",
                willChange: "transform, opacity",
              }}
              aria-hidden="true"
            >
              {char}
            </span>
          ))}
        </h2>
      </div>

      {/* Description */}
      <p
        ref={descRef}
        className="font-sans text-sm md:text-base text-brand-muted/70 mt-6 md:mt-8 max-w-md leading-relaxed tracking-wide opacity-0 select-none"
      >
        {current.description}
      </p>

      {/* Controls */}
      <div
        ref={controlsRef}
        className="flex items-center gap-6 mt-10 md:mt-14 opacity-0"
      >
        <button
          onClick={prev}
          className="w-11 h-11 rounded-full border border-white/[0.08] flex items-center justify-center text-white/40 hover:text-brand-cream hover:border-brand-cream/20 hover:bg-white/[0.03] transition-all duration-300 cursor-none"
          aria-label="Previous"
          data-hover
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>

        {/* Counter */}
        <div className="flex items-center gap-3 select-none">
          <span className="font-mono text-[11px] text-white/35 tracking-[0.3em] tabular-nums select-none">
            {String(activeIndex + 1).padStart(2, "0")}
          </span>
          <span className="w-6 h-px bg-brand-accent/30" />
          <span className="font-mono text-[11px] text-white/40 tracking-[0.3em] tabular-nums select-none">
            {String(slides.length).padStart(2, "0")}
          </span>
        </div>

        <button
          onClick={next}
          className="w-11 h-11 rounded-full border border-white/[0.08] flex items-center justify-center text-white/40 hover:text-brand-cream hover:border-brand-cream/20 hover:bg-white/[0.03] transition-all duration-300 cursor-none"
          aria-label="Next"
          data-hover
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Carousel;